// Live panels for the team: Azure (Operations) and GitHub (Engineering). Read only.
import { useEffect, useState } from "react";
import { Icon, Pill, useStudio } from "./ui";
import { desktop } from "../lib/desktop";
import { cleanError, desktopLive, openLink } from "../lib/m365";

type Sub = { id: string; name: string; state: string };
type Overview = { resources: number; resourceGroups: number; topTypes: { type: string; provider: string; count: number }[]; cost: { total: number; currency: string; byService: { service: string; cost: number }[] } | null; costNote: string };

const money = (n: number, c: string) => new Intl.NumberFormat(undefined, { style: "currency", currency: c || "USD", maximumFractionDigits: 0 }).format(n);

/** Azure: subscriptions, resources and month-to-date costs. Shows nothing until Azure is connected. */
export function AzureLive() {
  const { go } = useStudio();
  const [connected, setConnected] = useState<boolean | null>(null);
  const [subs, setSubs] = useState<Sub[] | null>(null);
  const [sub, setSub] = useState("");
  const [ov, setOv] = useState<Overview | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  useEffect(() => { if (desktopLive()) void desktop!.azure.status().then(setConnected).catch(() => setConnected(false)); }, []);
  useEffect(() => {
    if (!connected) return;
    desktop!.azure.run<Sub[]>("azure.subscriptions").then((s) => { setSubs(s); setSub(s[0]?.id ?? ""); }).catch((e) => setError(cleanError(e)));
  }, [connected]);
  useEffect(() => {
    if (!sub) return;
    setLoading(true); setError(""); setOv(null);
    desktop!.azure.run<Overview>("azure.overview", { subscriptionId: sub }).then(setOv).catch((e) => setError(cleanError(e))).finally(() => setLoading(false));
  }, [sub]);
  if (!desktopLive() || connected === null) return null;
  if (!connected) return <div className="card row" style={{ justifyContent: "space-between" }}><span className="mute">Connect Azure to see real subscriptions, resources and this month's costs here.</span><button className="btn primary" onClick={() => go("connections")}>Open Connections</button></div>;
  return (
    <div className="card stack" style={{ gap: 12 }}>
      <div className="row" style={{ justifyContent: "space-between" }}>
        <h2>Azure</h2>
        {subs && subs.length > 1 && <div className="row" style={{ gap: 6 }}>{subs.map((s) => <button key={s.id} className={`chip ${sub === s.id ? "on" : ""}`} onClick={() => setSub(s.id)}>{s.name}</button>)}</div>}
      </div>
      {subs && !subs.length && <p className="faint">Your Azure role doesn't include any subscriptions yet.</p>}
      {error && <p style={{ color: "var(--bad)" }}>{error}</p>}
      {loading && <p className="faint">Loading…</p>}
      {ov && (
        <div className="grid g3">
          <div className="stat"><span className="faint">This month so far</span><b className="num">{ov.cost ? money(ov.cost.total, ov.cost.currency) : "n/a"}</b>{!ov.cost && <small className="faint">{ov.costNote}</small>}</div>
          <div className="stat"><span className="faint">Resources</span><b className="num">{ov.resources}</b><small className="faint">in {ov.resourceGroups} resource groups</small></div>
          <div className="stat"><span className="faint">Most used</span><b style={{ fontSize: 16 }}>{ov.topTypes[0]?.type ?? "n/a"}</b><small className="faint">{ov.topTypes.slice(1, 4).map((t) => t.type).join(", ")}</small></div>
        </div>
      )}
      {ov?.cost && !!ov.cost.byService.length && (
        <div className="hbars">{ov.cost.byService.map((s) => (
          <div className="hbar" key={s.service}><span>{s.service}</span><div className="track"><span className="fill" style={{ width: `${(s.cost / (ov.cost!.byService[0].cost || 1)) * 100}%` }} /></div><span className="v num">{money(s.cost, ov.cost!.currency)}</span></div>))}</div>
      )}
      <span className="live">Live from Azure · read only</span>
    </div>
  );
}

type Repo = { name: string; private: boolean; pushed: string; url: string; issues: number; branch: string };
type Pull = { repo: string; number: number; title: string; author: string; url: string; draft: boolean; updated: string; checks: "passing" | "failing" | "running" | "none" };

/** GitHub: repositories and open pull requests with checks. Null when not connected (the sample shows instead). */
export function useGitHubLive() {
  const [state, setState] = useState<string | null>(null);
  const [repos, setRepos] = useState<Repo[] | null>(null);
  const [pulls, setPulls] = useState<Pull[] | null>(null);
  const [error, setError] = useState("");
  useEffect(() => { if (desktopLive()) void desktop!.github.status().then((s) => setState(s.state)).catch(() => setState("off")); }, []);
  useEffect(() => {
    if (state !== "connected") return;
    desktop!.github.repos().then(setRepos).catch((e) => setError(cleanError(e)));
    desktop!.github.pulls().then(setPulls).catch((e) => setError(cleanError(e)));
  }, [state]);
  return { state, repos, pulls, error };
}

const CHECK: Record<Pull["checks"], [string, string]> = { passing: ["ok", "Passing"], failing: ["bad", "Failing"], running: ["warn", "Running"], none: ["info", "No checks"] };

export function GitHubLive({ live }: { live: ReturnType<typeof useGitHubLive> }) {
  const { repos, pulls, error } = live;
  return (
    <>
      {error && <p style={{ color: "var(--bad)" }}>{error}</p>}
      <div className="split">
        <div className="card tbl-wrap"><table><thead><tr><th>Repository</th><th>Last push</th><th>Open issues</th></tr></thead><tbody>
          {!repos ? <tr><td colSpan={3} className="faint">Loading…</td></tr> : repos.map((r) => (
            <tr key={r.name}><td><button className="filebtn" style={{ padding: 0 }} onClick={() => openLink(r.url)}><Icon name="repo" size={16} /><b>{r.name}</b></button></td><td className="faint">{new Date(r.pushed).toLocaleDateString()}</td><td className="num">{r.issues}</td></tr>))}
        </tbody></table></div>
        <div className="card"><h2 style={{ marginBottom: 6 }}>Open pull requests</h2>
          <div className="list">{!pulls ? <p className="faint">Loading…</p> : !pulls.length ? <p className="faint">No open pull requests.</p> : pulls.map((p) => (
            <button className="li linkish" key={p.url} onClick={() => openLink(p.url)} style={{ background: "none", border: 0, color: "inherit", textAlign: "left", width: "100%" }}>
              <span className="ico"><Icon name="pr" /></span>
              <div className="grow"><b>#{p.number} · {p.title}</b><div className="faint" style={{ fontSize: 12 }}>{p.repo.split("/")[1]} · {p.author}{p.draft ? " · draft" : ""}</div></div>
              <Pill tone={CHECK[p.checks][0]}>{CHECK[p.checks][1]}</Pill>
            </button>))}</div></div>
      </div>
      <span className="live">Live from GitHub · read only</span>
    </>
  );
}
